import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Lock, TriangleAlert, Download, BookOpen, HandCoins, Newspaper, Fingerprint, Palette, Camera, CalendarDays } from "lucide-react";
import { SubpageHeader } from "../components/SubpageHeader";
import { Reveal } from "../components/Reveal";
import { useLanguage, LANGS } from "../i18n/LanguageContext";
import { useSEO, breadcrumbLd } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
    pt: { title: "CENTRO DE RECURSOS", intro: "Documentos oficiais do Fados à Mesa, reservados a parceiros, patrocinadores e imprensa.", pass: "Palavra-passe", unlock: "Desbloquear", wrong: "Palavra-passe incorreta", note: "A palavra-passe é partilhada pela organização, em qualquer língua.", soon: "Brevemente disponível", version: "Versão publicada", revision: "Última revisão", download: "Download", unlocked: "Centro de Recursos desbloqueado.", unlockedSub: "Documentos oficiais prontos para consulta e download." },
    en: { title: "RESOURCE CENTER", intro: "Official Fados à Mesa documents, reserved for partners, sponsors and press.", pass: "Password", unlock: "Unlock", wrong: "Incorrect password", note: "The password is shared by the organization, in any language.", soon: "Coming soon", version: "Published version", revision: "Last revision", download: "Download", unlocked: "Resource Center unlocked.", unlockedSub: "Official documents ready to view and download." },
    fr: { title: "CENTRE DE RESSOURCES", intro: "Documents officiels de Fados à Mesa, réservés aux partenaires, sponsors et à la presse.", pass: "Mot de passe", unlock: "Débloquer", wrong: "Mot de passe incorrect", note: "Le mot de passe est partagé par l'organisation, dans toutes les langues.", soon: "Bientôt disponible", version: "Version publiée", revision: "Dernière révision", download: "Télécharger", unlocked: "Centre de Ressources débloqué.", unlockedSub: "Documents officiels prêts à consulter et à télécharger." },
    de: { title: "MEDIENCENTER", intro: "Offizielle Dokumente von Fados à Mesa, reserviert für Partner, Sponsoren und Presse.", pass: "Passwort", unlock: "Freischalten", wrong: "Falsches Passwort", note: "Das Passwort wird von der Organisation in allen Sprachen geteilt.", soon: "Bald verfügbar", version: "Veröffentlichte Version", revision: "Letzte Überarbeitung", download: "Download", unlocked: "Mediencenter freigeschaltet.", unlockedSub: "Offizielle Dokumente bereit zur Ansicht und zum Download." },
    it: { title: "CENTRO RISORSE", intro: "Documenti ufficiali di Fados à Mesa, riservati a partner, sponsor e stampa.", pass: "Password", unlock: "Sblocca", wrong: "Password errata", note: "La password è condivisa dall'organizzazione, in qualsiasi lingua.", soon: "Presto disponibile", version: "Versione pubblicata", revision: "Ultima revisione", download: "Scarica", unlocked: "Centro Risorse sbloccato.", unlockedSub: "Documenti ufficiali pronti per la consultazione e il download." },
};

const LANG_LABELS = { pt: "Português", en: "English", fr: "Français", de: "Deutsch", it: "Italiano" };

const DOCS = [
    { id: "project-book", icon: BookOpen, title: "Project Book" },
    { id: "dossier-patrocinios", icon: HandCoins, title: "Dossier de Patrocínios" },
    { id: "press-kit", icon: Newspaper, title: "Press Kit" },
    { id: "logotipos", icon: Fingerprint, title: "Logótipos Oficiais" },
    { id: "manual-identidade", icon: Palette, title: "Manual de Identidade Visual" },
    { id: "fotografias", icon: Camera, title: "Fotografias Oficiais" },
    { id: "programa-oficial", icon: CalendarDays, title: "Programa Oficial" },
];

const FILES = {
    "project-book": { pt: { version: "1.0", revision: "02/09/2026" } },
};

export default function RecursosPage() {
    const [password, setPassword] = useState("");
    const [token, setToken] = useState(() => sessionStorage.getItem("rc_token"));
    const [erro, setErro] = useState(null);
    const { lang, setLang, t: gt } = useLanguage();
    const [aberto, setAberto] = useState(null);
    useSEO({
        title: gt.seo.recursos.title,
        description: gt.seo.recursos.desc,
        jsonLd: breadcrumbLd([{ name: gt.bc.home, path: "/" }, { name: gt.bc.recursos, path: "/recursos" }]),
    });
    const [aDescarregar, setADescarregar] = useState(null);
    const t = T[lang];

    const desbloquear = async (e) => {
        e.preventDefault();
        setErro(null);
        try {
            const { data } = await axios.post(`${API}/recursos/desbloquear`, { password });
            sessionStorage.setItem("rc_token", data.token);
            setToken(data.token);
        } catch (err) {
            setErro(err.response?.data?.detail || t.wrong);
        }
    };

    const descarregar = async (docId, docLang) => {
        const key = `${docId}-${docLang}`;
        setADescarregar(key);
        try {
            const resp = await axios.get(`${API}/recursos/download`, {
                params: { doc: docId, lang: docLang },
                headers: { Authorization: `Bearer ${token}` },
                responseType: "blob",
            });
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `fados-a-mesa-${docId}-${docLang}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            if (err.response?.status === 401) {
                sessionStorage.removeItem("rc_token");
                setToken(null);
            }
        } finally {
            setADescarregar(null);
        }
    };

    return (
        <div className="min-h-screen bg-[#070503] text-[#efe6d6] relative overflow-hidden" data-testid="recursos-page">
            <div className="absolute inset-0 pointer-events-none">
                <img loading="lazy" decoding="async" src="/images/ambiente.webp" alt="" aria-hidden="true" className="w-full h-full object-cover opacity-25 blur-md scale-110" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#070503]/60 via-[#070503]/80 to-[#070503]" />
            </div>
            <SubpageHeader />
            <motion.main
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                className="relative z-10 mx-auto max-w-[1100px] px-5 sm:px-10 pt-44 sm:pt-36 pb-24"
            >
                <Reveal className="text-center">
                    {!token && <Lock size={26} strokeWidth={1.2} className="text-[#c9a24b] mx-auto" />}
                    <h1 className="font-display font-medium gold-text text-4xl sm:text-5xl tracking-[0.08em] mt-6" data-testid="recursos-title">
                        {t.title}
                    </h1>
                    <div className="flex justify-center mt-4">
                        <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-16 opacity-90" />
                    </div>
                    <p className="font-display font-light text-lg text-[#efe6d6]/70 leading-relaxed max-w-2xl mx-auto mt-6">{t.intro}</p>
                </Reveal>

                {!token ? (
                    <Reveal delay={0.15} className="mt-10 max-w-md mx-auto">
                        <form onSubmit={desbloquear} className="border border-[#c9a24b]/30 bg-[#0a0705]/80 p-8 space-y-6" data-testid="recursos-form">
                            <div>
                                <label htmlFor="rc-pass" className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#d19a3f] block mb-2.5">
                                    {t.pass}
                                </label>
                                <input
                                    id="rc-pass"
                                    type="password"
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    data-testid="recursos-password"
                                    className="w-full bg-[#070503] border border-[#c9a24b]/30 px-4 py-3 font-light text-[#efe6d6] focus:border-[#c9a24b] focus:outline-none transition-colors duration-300"
                                    placeholder="••••••••"
                                />
                            </div>
                            {erro && (
                                <p className="flex items-center gap-2 text-sm font-light text-[#e0a0a0]" data-testid="recursos-erro">
                                    <TriangleAlert size={16} /> {erro}
                                </p>
                            )}
                            <button
                                type="submit"
                                data-testid="recursos-unlock"
                                className="w-full font-caps text-xs tracking-[0.3em] uppercase text-[#070503] bg-[#c9a24b] px-8 py-3.5 hover:bg-[#ecd49b] transition-colors duration-400"
                            >
                                {t.unlock}
                            </button>
                            <p className="text-center text-xs font-light text-[#efe6d6]/40">{t.note}</p>
                        </form>
                    </Reveal>
                ) : (
                    <>
                        <Reveal delay={0.1} className="mt-8">
                            <div className="flex justify-center gap-3 sm:gap-5" data-testid="recursos-langs">
                                {LANGS.map((l) => (
                                    <button
                                        key={l}
                                        onClick={() => setLang(l)}
                                        data-testid={`recursos-lang-${l}`}
                                        className={`font-caps text-[11px] tracking-[0.2em] uppercase pb-1 transition-colors duration-300 ${
                                            lang === l ? "text-[#c9a24b] border-b border-[#c9a24b]" : "text-[#efe6d6]/45 hover:text-[#efe6d6]"
                                        }`}
                                    >
                                        {l.toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </Reveal>

                        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="recursos-grid">
                            {DOCS.map((d, i) => {
                                const files = FILES[d.id];
                                const isOpen = aberto === d.id;
                                return (
                                    <Reveal key={d.id} delay={i * 0.05}>
                                        <div
                                            className={`border bg-[#0a0705]/80 transition-colors duration-500 h-full flex flex-col ${
                                                isOpen ? "border-[#c9a24b]/60" : "border-[#c9a24b]/20 hover:border-[#c9a24b]/45"
                                            }`}
                                            data-testid={`recurso-card-${d.id}`}
                                        >
                                            <button
                                                onClick={() => setAberto(isOpen ? null : d.id)}
                                                className="p-7 text-left flex-1"
                                                data-testid={`recurso-open-${d.id}`}
                                            >
                                                <d.icon size={28} strokeWidth={1.1} className="text-[#c9a24b]" />
                                                <h2 className="font-display font-light text-xl sm:text-2xl text-[#efe6d6] mt-4 leading-snug">{d.title}</h2>
                                                {!files && (
                                                    <p className="font-caps text-[10px] tracking-[0.25em] uppercase text-[#efe6d6]/40 mt-4" data-testid={`recurso-soon-${d.id}`}>
                                                        {t.soon}
                                                    </p>
                                                )}
                                            </button>
                                            {isOpen && files && (
                                                <div className="border-t border-[#c9a24b]/15 px-7 py-5 space-y-4" data-testid={`recurso-versoes-${d.id}`}>
                                                    {Object.entries(files).map(([l, meta]) => (
                                                        <div key={l} className="flex items-center justify-between gap-3" data-testid={`recurso-versao-${d.id}-${l}`}>
                                                            <div>
                                                                <p className="font-caps text-xs tracking-[0.15em] uppercase text-[#ecd49b]">{LANG_LABELS[l]}</p>
                                                                <p className="text-xs font-light text-[#efe6d6]/50 mt-1">
                                                                    {t.version} {meta.version} · {t.revision} {meta.revision}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => descarregar(d.id, l)}
                                                                disabled={aDescarregar === `${d.id}-${l}`}
                                                                data-testid={`recurso-download-${d.id}-${l}`}
                                                                className="inline-flex items-center gap-2 font-caps text-[10px] tracking-[0.25em] uppercase text-[#070503] bg-[#c9a24b] px-4 py-2.5 hover:bg-[#ecd49b] transition-colors duration-300 disabled:opacity-50 shrink-0"
                                                            >
                                                                <Download size={13} /> {t.download}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Reveal>
                                );
                            })}
                        </div>
                    </>
                )}
            </motion.main>
        </div>
    );
}
